import { TaskType } from "./enums";

/**
 * Checks if a section or its children contain any historical selection IDs.
 * Used to determine if we should preserve the structure for editing existing records.
 *
 * @param {Object} section - The section to check
 * @param {Array} historicalTaskIds - Array of selected task/stage IDs to look for
 * @returns {boolean} True if any historical tasks are found
 *
 */
const checkForHistoricalTasks = (section, historicalTaskIds) => {
  if (!historicalTaskIds || historicalTaskIds.length === 0) return false;

  // Check direct stage match (for stage-level allocations)
    if (historicalTaskIds.includes(section.id)) return true;

    // Check direct tasks
  if (section.tasks && Array.isArray(section.tasks)) {
    const hasDirectMatch = section.tasks.some((task) =>
      historicalTaskIds.includes(task.id),
    );
    if (hasDirectMatch) return true;
  }

  // Recursively check nested stages
  if (section.stages && Array.isArray(section.stages)) {
    return section.stages.some((nestedStage) =>
      checkForHistoricalTasks(nestedStage, historicalTaskIds),
    );
  }

  return false;
};

/**
 * Filters out field-ops excluded tasks unless they are historical selections.
 *
 * @param {Array} tasks - Stage task list
 * @param {Array} historicalTaskIds - Task/stage IDs that must be preserved
 * @returns {Array} Filtered task list
 */
const filterExcludedTasks = (tasks = [], historicalTaskIds = []) => {
    if (!Array.isArray(tasks)) return [];
    return tasks.filter(task =>
        !task.excludeFromFieldOps || historicalTaskIds.includes(task.id)
    );
};

/**
 * Collects all descendant task and stage IDs for a given stage.
 * Used for hierarchical selection where selecting a parent includes all children.
 *
 * @param {Object} stage - The stage to collect descendants from
 * @returns {Object} { taskIds: Array<string>, stageIds: Array<string> }
 */
export const collectDescendantIds = (stage) => {
  const taskIds = [];
  const stageIds = [stage.id];

  // Collect direct tasks
  if (stage.tasks && Array.isArray(stage.tasks)) {
    taskIds.push(...stage.tasks.map((t) => t.id));
  }

  // Recursively collect from child stages
  if (stage.stages && Array.isArray(stage.stages)) {
    stage.stages.forEach((childStage) => {
      const childIds = collectDescendantIds(childStage);
      taskIds.push(...childIds.taskIds);
      stageIds.push(...childIds.stageIds);
    });
  }

  return { taskIds, stageIds };
};

/**
 * Universal Section Filtering Based on Attribution Level Flag
 *
 * Filters sections based on attribution level flags while preserving visibility
 * of sections containing already-selected tasks for editing existing records.
 *
 * UNIFIED LOGIC (applies to PO, WO, and Timesheets):
 * - Shows ALL sections (whether flagged or not)
 * - For sections with is_attribution_level = true:
 *   → Hide their tasks (children)
 *   → Hide their child sections
 *   → Section itself becomes selectable (stored in stage_id)
 *   → EXCEPT: If section contains tasks in alwaysIncludeTaskIds (edit mode),
 *     preserve ALL children for editing existing records
 * - For sections with is_attribution_level = false/null:
 *   → Show their tasks normally
 *   → Show their child sections normally
 *   → Tasks are selectable (stored in task_id)
 *
 * BACKWARD COMPATIBILITY:
 * - When editing existing PO/WO/Timesheets, pass already-selected task IDs via alwaysIncludeTaskIds
 * - Sections containing these tasks will preserve full visibility for editing
 * - New PO/WO/Timesheets pass empty array, getting new attribution behavior
 *
 * @param {Array} sections - The hierarchical project sections/stages
 * @param {Array} alwaysIncludeTaskIds - Task IDs already selected (for edit mode)
 * @returns {Array} Filtered sections with children hidden for flagged sections
 */
export const filterLockedSections = (sections, alwaysIncludeTaskIds = []) => {
  // Guard clause: return empty array if input is not valid
  if (!Array.isArray(sections)) return [];

  // Process all sections, hiding children for flagged ones
  return sections.map((section) => {
    // Hide fully excluded sections for new allocations.
        // Preserve full structure when historical selections exist.
        if (section.exclude_from_field_ops === true) {
            const hasHistoricalTasks = checkForHistoricalTasks(section, alwaysIncludeTaskIds);

            if (hasHistoricalTasks && alwaysIncludeTaskIds.length > 0) {
                const preservedStages = section.stages
                    ? filterLockedSections(section.stages, alwaysIncludeTaskIds)
                    : [];
                return {
                    ...section,
                    isHistoricalView: true,
                    tasks: filterExcludedTasks(section.tasks, alwaysIncludeTaskIds),
                    stages: preservedStages
                };
            }

            return null;
        }

        // If this section is flagged for attribution
    if (section.is_attribution_level === true) {
      // Check if this section contains any historical tasks from existing PO/WO
      const hasHistoricalTasks = checkForHistoricalTasks(
        section,
        alwaysIncludeTaskIds,
      );

      if (hasHistoricalTasks && alwaysIncludeTaskIds.length > 0) {
        // Historical/Edit mode - preserve full structure for viewing/editing
        const preservedStages = section.stages
          ? filterLockedSections(section.stages, alwaysIncludeTaskIds) // Recursively process with historical IDs
          : [];
        return {
          ...section,
          isHistoricalView: true, // Flag for potential UI indicators
          tasks: filterExcludedTasks(section.tasks, alwaysIncludeTaskIds), // Keep tasks for editing
          stages: preservedStages,
        };
      }

      return {
        ...section,
        tasks: [], // Hide tasks within flagged sections
        stages: [], // Hide child sections
      };
    }

    // Section is NOT flagged - recursively process child sections
    return {
      ...section,
      tasks: filterExcludedTasks(section.tasks, alwaysIncludeTaskIds), // Keep tasks
      stages: section.stages
        ? filterLockedSections(section.stages, alwaysIncludeTaskIds) // Recursively process children
        : [],
    };
  }).filter(Boolean);
};

/**
 * Converts hierarchical stage/task data to react-select format with tree structure support.
 *
 * Tree Structure Enhancement:
 * - Each option includes `depth` (nesting level, 0-based) and `displayName` (item's own name)
 * - `isLastChild` indicates if the item is the last sibling at its level
 * - `parentIsLastFlags` array tracks which parent levels are "last" for proper tree line rendering
 * - These properties enable the MultiSelect component to render tree lines (│ ├ └)
 *
 * @param {Array} data - Hierarchical stage data
 * @param {string} ignoreStageId - Stage ID to ignore (optional)
 * @param {boolean} taskIncluded - Whether to include tasks in the output
 * @returns {Array} Flat array of options with tree structure metadata
 */
export function convertToReactSelectFormat(
  data,
  ignoreStageId,
  taskIncluded = false,
  allowSectionSelection = false,
) {
  /**
   * Recursively converts a stage to react-select group format with tree metadata.
   *
   * @param {Object} stage - The stage object to convert
   * @param {string} parentName - Full path of parent (e.g., "LEVEL 2 > SEWER")
   * @param {number} depth - Current nesting depth (0-based)
   * @param {boolean} isLastChild - Whether this stage is the last sibling
   * @param {Array<boolean>} parentIsLastFlags - Array tracking if each parent level is "last"
   */

  function convertStageToGroup(
    stage,
    parentName = "",
    depth = 0,
    isLastChild = true,
    parentIsLastFlags = [],
  ) {
    let groupLabel = parentName ? `${parentName} > ${stage.name}` : stage.name;
    const displayName = stage.name; // Just the current item's name for tree display

    // Case 1: Only Sections (taskIncluded = false)
    // Every section becomes a selectable option, and we flatly recurse children.
    if (!taskIncluded) {
      let options = [];
      if (stage.id !== ignoreStageId) {
        options.push({
          value: stage.id,
          label: groupLabel,
          displayName: displayName,
          depth: depth,
          isLastChild: isLastChild,
          parentIsLastFlags: parentIsLastFlags,
          isStage: true,
          isAttributionLevel: !!stage.is_attribution_level,
        });
      }

      if (stage.stages && stage.stages.length > 0) {
        const childCount = stage.stages.length;
        const childParentFlags = [...parentIsLastFlags, isLastChild];
        stage.stages.forEach((subStage, index) => {
          const childIsLast = index === childCount - 1;
          const children = convertStageToGroup(
            subStage,
            groupLabel,
            depth + 1,
            childIsLast,
            childParentFlags,
          );

          if (Array.isArray(children)) {
            options = options.concat(children);
          } else if (children.options) {
            options = options.concat(children.options);
          } else if (children.value) {
            options.push(children);
          }
        });
      }
      return { options };
    }

    // Case 2: Sections and Tasks (taskIncluded = true)
    if (stage.is_attribution_level && !stage.isHistoricalView) {
      // Normal flagged section - return as selectable option, skip children
      return {
        value: stage.id,
        label: groupLabel,
        displayName: displayName,
        isStage: true,
        isAttributionLevel: true, // Explicitly true
        stagePath: parentName,
        depth: depth,
        isLastChild: isLastChild,
        parentIsLastFlags: parentIsLastFlags,
      };
    }

    // Historical view: flagged section with preserved children - process normally

    let group = {
      label: groupLabel,
      options: [],
    };

    if (stage.is_attribution_level && stage.isHistoricalView) {
      // Add the section itself as a selectable option
      group.options.push({
        value: stage.id,
        label: groupLabel,
        displayName: displayName,
        isStage: true,
        isAttributionLevel: true, // Explicitly true
        stagePath: parentName,
        depth: depth,
        isLastChild: isLastChild,
        parentIsLastFlags: parentIsLastFlags,
      });
    }

    // Standard behavior
    if (stage.stages && stage.stages.length > 0) {
      // Add parent section header when it has child stages (so parent name is visible)
      // Skip if this section was already added as a selectable stage above
      if (!(stage.is_attribution_level && stage.isHistoricalView)) {
        // Collect descendant IDs for hierarchical selection
        const descendants = allowSectionSelection
          ? collectDescendantIds(stage)
          : null;
        group.options.push({
          label: groupLabel,
          displayName: displayName,
          value: allowSectionSelection ? stage.id : `section-${stage.id}`,
          isDisabled: !allowSectionSelection, // Selectable when allowSectionSelection is true
          depth: depth,
          isLastChild: isLastChild,
          parentIsLastFlags: parentIsLastFlags,
          isSectionHeader: !allowSectionSelection, // Only treat as header when not selectable
          isSection: allowSectionSelection, // Mark as section when selectable
          isStage: allowSectionSelection,
          isAttributionLevel: !!stage.is_attribution_level,
          stagePath: parentName,
          hasChildren: true,
          ...(descendants && {
            childTaskIds: descendants.taskIds,
            childStageIds: descendants.stageIds,
          }),
        });
      }

      const childCount = stage.stages.length;
      stage.stages.forEach((subStage, index) => {
        const childIsLast = index === childCount - 1;
        const childParentFlags = [...parentIsLastFlags, isLastChild];
        let result = convertStageToGroup(
          subStage,
          groupLabel,
          depth + 1,
          childIsLast,
          childParentFlags,
        );
        if (result.value && result.label) {
          group.options.push(result);
        } else {
          group.options = group.options.concat(result.options);
        }
      });
    } else {
      if (stage.id === ignoreStageId) return group;
    }

    if (stage.tasks && stage.tasks.length > 0) {
      // Section header (disabled) - shows the section name in the dropdown
      // Skip if this section was already added as a selectable stage above
      if (!(stage.is_attribution_level && stage.isHistoricalView)) {
        // Collect descendant IDs for hierarchical selection
        const descendants = allowSectionSelection
          ? collectDescendantIds(stage)
          : null;
        group.options.unshift({
          label: groupLabel,
          displayName: displayName,
          value: allowSectionSelection ? stage.id : `section-${stage.id}`,
          isDisabled: !allowSectionSelection, // Selectable when allowSectionSelection is true
          depth: depth,
          isLastChild: isLastChild,
          parentIsLastFlags: parentIsLastFlags,
          isSectionHeader: !allowSectionSelection, // Only treat as header when not selectable
          isSection: allowSectionSelection, // Mark as section when selectable
          isStage: allowSectionSelection,
          isAttributionLevel: !!stage.is_attribution_level,
          stagePath: parentName,
          hasChildren: true,
          ...(descendants && {
            childTaskIds: descendants.taskIds,
            childStageIds: descendants.stageIds,
          }),
        });
      }

      const taskCount = stage.tasks.length;
      const childParentFlags = [...parentIsLastFlags, isLastChild];
      stage.tasks.forEach((task, index) => {
        const taskIsLast = index === taskCount - 1;
        // Only include variations if its optionProposalStatus variable exists AND is PENDING or APPROVED
        // Include all regular tasks, but only filter variations by status
        if (
          !task.isVariation ||
          (task.optionProposalStatus &&
            ["PENDING", "APPROVED"].includes(task.optionProposalStatus))
        ) {
          group.options.push({
            value: task.id,
            label: task.name,
            displayName: task.name,
            key: task.name,
            stagePath: groupLabel,
            taskType: task.isVariation ? TaskType.VARIATION : TaskType.TASK,
            depth: depth + 1,
            isLastChild: taskIsLast,
            parentIsLastFlags: childParentFlags,
            isTask: true,
          });
        }
      });
    }
    return group;
  }

  let result = [];
  const itemCount = data.length;
  data.forEach((item, index) => {
    const isLast = index === itemCount - 1;
    const group = convertStageToGroup(item, "", 0, isLast, []);
    if (group.value && group.label) {
      result.push(group);
    } else if (group.options) {
      result = result.concat(group.options);
    }
  });
  return result;
}
